import {
  COAL_ORE,
  COBBLESTONE,
  CRAFTING_TABLE,
  GLASS,
  GOLD_ORE,
  IRON_ORE,
  LOG,
  PLANKS,
  SAND,
  STONE,
} from "./blocks";
import {
  CHARCOAL,
  COAL,
  COOKED_BEEF,
  COOKED_MUTTON,
  COOKED_PORKCHOP,
  GOLD_INGOT,
  IRON_INGOT,
  RAW_BEEF,
  RAW_MUTTON,
  RAW_PORKCHOP,
  STICK,
} from "./items";

/**
 * Smelting recipes, fuel values and timing.
 *
 * A furnace burns one fuel item at a time; `fuelValue` is the number of items
 * that fuel can smelt. Recipe results are ordinary item ids, so block and
 * non-block products use the same path.
 */

/** Seconds one item takes to smelt. */
export const SMELT_SECONDS = 8;

export interface SmeltingRecipe {
  readonly input: number;
  readonly result: number;
  readonly count: number;
}

export const SMELTING_RECIPES: readonly SmeltingRecipe[] = [
  { input: IRON_ORE, result: IRON_INGOT, count: 1 },
  { input: GOLD_ORE, result: GOLD_INGOT, count: 1 },
  { input: SAND, result: GLASS, count: 1 },
  { input: COBBLESTONE, result: STONE, count: 1 },
  { input: LOG, result: CHARCOAL, count: 1 },
  { input: COAL_ORE, result: COAL, count: 1 },
  { input: RAW_BEEF, result: COOKED_BEEF, count: 1 },
  { input: RAW_PORKCHOP, result: COOKED_PORKCHOP, count: 1 },
  { input: RAW_MUTTON, result: COOKED_MUTTON, count: 1 },
];

const BY_INPUT = new Map<number, SmeltingRecipe>(
  SMELTING_RECIPES.map((recipe) => [recipe.input, recipe]),
);

/** How many items each fuel smelts. Unknown ids are not fuel. */
export const FUEL_VALUES: Readonly<Record<number, number>> = {
  [COAL]: 8,
  [CHARCOAL]: 8,
  [PLANKS]: 1,
  [LOG]: 1,
  [CRAFTING_TABLE]: 1,
  [STICK]: 1,
};

export function findSmeltingRecipe(inputId: number): SmeltingRecipe | null {
  return BY_INPUT.get(inputId) ?? null;
}

export function isSmeltable(id: number): boolean {
  return BY_INPUT.has(id);
}

export function fuelValue(id: number): number {
  return FUEL_VALUES[id] ?? 0;
}

export function isFuel(id: number): boolean {
  return fuelValue(id) > 0;
}
